import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import utc from 'dayjs/plugin/utc';
import {
    ArrowLeftRight,
    CheckCircle2,
    Eye,
    Pencil,
    RotateCcw,
    Search,
    Trash2,
    X
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dispatchCashFlowInvalidate } from '../../common/cashFlowEvents';
import { formatCurrency } from '../../common/formatters';
import { httpClient } from '../../common/httpclient';
import { isEditableExpenseStatus, toIsoYyyyMmDd } from '../../common/transactionDates';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import DesktopPeriodSelector from '../../component/common/DesktopPeriodSelector';
import DialogForm from '../../component/dialog/DialogForm';
import NewItemButton from '../../component/common/NewItemButton';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import StatusChip from '../../component/common/StatusChip';
import DefaultTable from '../../component/table/DefaultTable';
import { protoIcon } from '../../component/common/ProtoIcon';
import { MobileTransactionsList } from '../../component/finance/MobileFinance';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import usePermissions from '../../hooks/usePermissions';
import { EXPENSE_STATUS, updateExpenseStatus } from '../../services/expenseStatusService';
import TransactionForm from './Form';
import TransactionEditForm from './TransactionEditForm';

dayjs.extend(utc);

// Lista de transações — DESIGN-SYSTEM.md, mesma estrutura de
// `pages/services/List.js`/`pages/cashregisters/List.js` (`report-toolbar` +
// `chips-row` + `DefaultTable`). Ref: `prototipo-lista-transacoes.html`.
//
// Endpoints reais (`fv-api/transaction`, `config/router.go`):
//   GET  /transactions/query   — lista (start_month/end_month DD/MM/YYYY, kind)
//   POST /transactions/revenue — criar receita (via `Form.js`, item irmão)
//   POST /transactions/expense — criar despesa (via `Form.js`, item irmão)
//   PUT  /transactions/expense — status (Pendente <-> Prestado Contas; exige
//                                payment_date quando vira Prestado Contas)
//   PUT  /transactions/{id}    — editar lançamento, só permitido com despesa
//                                Pendente ou Vencido (`TransactionEditForm.js`)
// Não existe GET /transactions/{id} nem DELETE /transactions/{id} no
// backend hoje.
//
// "Nova transação"/"Editar" abrem o drawer real do formulário de transação
// (`Form.js`/`TransactionEditForm.js`, item irmão "Formulário de transação",
// restilizado em 2026-09-17 com `tx-type-cards`/`drawer-*` do protótipo —
// ver nota no topo desses dois arquivos). "Editar" (`openEditForRow`) decide
// a variante pela mesma regra do protótipo (`onEditTransaction`): despesa
// Pendente/Vencido abre a variante compacta (`TransactionEditForm`, único
// caso que `PUT /transactions/{id}` de fato persiste); Receita (qualquer
// status) ou Despesa já Prestado Contas abrem o formulário completo
// (`Form.js`, tipo travado) — que chama o **mesmo** `PUT /transactions/{id}`,
// não um endpoint novo. Como o repositório
// (`UpdateLancamentoDespesaPendenteOuVencido`) só persiste despesa
// Pendente/Vencido, esses dois casos sempre recebem um `409
// ErrTransactionNotEditable` real ao salvar — tratado com uma mensagem
// explícita e contextual em `Form.js` (mesmo padrão de "chamada real, falha
// graciosa" já usado por "Excluir" logo abaixo), não um bug: liberar edição
// de verdade para Receita/Despesa-Prestada exigiria mudar o contrato do
// backend, fora do escopo desta tarefa e sujeito a aprovação explícita.
//
// "Excluir": não existe `DELETE /transactions/{id}` no backend. Seguindo o
// mesmo precedente de `pages/cashregisters/List.js` (mesma situação com
// `DELETE /cashregister/{id}`), a ação é real (modal de confirmação +
// chamada de verdade) mas falha com uma mensagem explícita em vez de fingir
// sucesso — implementar o endpoint exige aprovação explícita (ver relatório
// final desta tarefa).
//
// Vencimento: a `TransactionResponse` de `GET /transactions/query`
// (`transaction/lister.go`) não devolve `due_date`, mesmo a query SQL já
// selecionando a coluna — só é descartado na serialização. O dialog
// "Visualizar" mostra o campo Vencimento para despesas (conforme o item),
// mas com "Não disponível" até esse campo ser exposto na API (mudança de
// contrato pequena, fora do escopo desta tarefa — precisa aprovação
// explícita, ver relatório final). O mesmo já limitava silenciosamente
// `TransactionEditForm.js` antes desta tarefa.
//
// Status "Vencido": além de Pendente/Prestado Contas (únicos 2 estados do
// protótipo), a API tem um 3º status real — `transaction.ExpenseStatusVencido`,
// setado por um job diário para despesas pendentes com vencimento passado.
// Mapeado aqui com um tom próprio (`StatusChip domain="expenseStatus"`,
// tom `danger`) em vez de escondido/fundido em "Pendente" para não mascarar
// dado real da API. Corrigido também um bug pré-existente: a lista antiga só
// mostrava a ação "Prestar contas"/"Editar" para status exatamente
// "pendente" (`ruleShow: ['pendente']`), nunca para "vencido" — mesmo a API
// (`transaction.UpdateExpenseStatus`/`UpdateLancamentoDespesaPendenteOuVencido`)
// permitir as duas operações também para despesas Vencidas. Corrigido para
// `ruleShow: ['pendente', 'vencido']`.

// Micro-label fixo do `tb-field` (DESIGN-SYSTEM.md §5.5/§9).
function TbLabel({ children }) {
    return (
        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );
}

// Item do `detail-grid` do dialog "Visualizar" — mesmo padrão local de
// `pages/services/View.js` (`DetailItem`), reaproveitado aqui como
// componente próprio do arquivo (não compartilhado entre telas).
function DetailItem({ label, value, muted = false, full = false }) {
    return (
        <Box sx={{ gridColumn: full ? '1 / -1' : 'auto' }}>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', mb: '4px' }}>
                {label}
            </Typography>
            {typeof value === 'string' ? (
                <Typography sx={{ fontSize: '13.5px', fontWeight: muted ? 500 : 600, color: muted ? 'text.disabled' : 'text.primary', lineHeight: 1.4 }}>
                    {value}
                </Typography>
            ) : (
                <Box sx={{ mt: '2px' }}>{value}</Box>
            )}
        </Box>
    );
}

function ValueText({ transaction }) {
    const isRevenue = transaction.type === 'receita';
    return (
        <Typography component="span" sx={{ fontSize: '13px', fontWeight: 800, color: isRevenue ? 'success.main' : 'error.main' }}>
            {isRevenue ? '+ ' : '− '}R$ {formatCurrency(transaction.amount)}
        </Typography>
    );
}

const MOBILE_PAGE_SIZE = 20;
const CheckIcon = protoIcon('check');
const UndoIcon = protoIcon('undo');
const EyeIcon = protoIcon('eye');
const EditIcon = protoIcon('edit');
const TrashIcon = protoIcon('trash');

export default function TransactionsList() {
    const theme = useTheme();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();
    const navigate = useNavigate();
    const location = useLocation();
    // Mobile: chip ativo da barra (Todos/Entradas/Saídas/Este mês) e "reload" para voltar à 1ª página.
    const [mobileChip, setMobileChip] = useState('all');
    const lastReloadRef = useRef(0);

    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    // Paginação server-side: `page` é 0-based (DefaultTable); a API usa 1-based.
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(8);
    const [totalCount, setTotalCount] = useState(0);
    // Somas (já formatadas pela API) sobre todo o conjunto filtrado, não só a página.
    const [totalRevenue, setTotalRevenue] = useState('0,00');
    const [totalExpense, setTotalExpense] = useState('0,00');
    const [reloadKey, setReloadKey] = useState(0);

    // Filtros — aplicados só ao clicar "Buscar"/"Limpar" (ou Enter na
    // Busca), mesma convenção de `prototipo-lista-transacoes.html`
    // (`applyFilters()`/`clearFilters()`) e de `pages/cashregisters/List.js`.
    const defaultStart = dayjs().startOf('month');
    const defaultEnd = dayjs().endOf('month');
    const [draftDateStart, setDraftDateStart] = useState(defaultStart);
    const [draftDateEnd, setDraftDateEnd] = useState(defaultEnd);
    const [draftType, setDraftType] = useState('tudo');
    const [draftStatus, setDraftStatus] = useState('');
    const [draftSearch, setDraftSearch] = useState('');

    const [type, setType] = useState('tudo');
    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');
    // Período efetivamente aplicado na última busca — alimenta o chip
    // "Período" (mesmo padrão de `pages/sales/List.js`).
    const [appliedPeriod, setAppliedPeriod] = useState({ start: defaultStart, end: defaultEnd });

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // drawer: Nova transação
    const [sheetOpen, setSheetOpen] = useState(false);
    // drawer: Editar (TransactionEditForm — variante compacta, despesa Pendente/Vencido)
    const [editSheetOpen, setEditSheetOpen] = useState(false);
    const [editTransaction, setEditTransaction] = useState(null);
    // drawer: Editar (Form.js — variante completa/tipo travado, Receita ou Despesa Prestado Contas)
    const [fullEditSheetOpen, setFullEditSheetOpen] = useState(false);
    const [fullEditTransaction, setFullEditTransaction] = useState(null);

    // dialog: Visualizar (somente leitura)
    const [viewOpen, setViewOpen] = useState(false);
    const [viewTransaction, setViewTransaction] = useState(null);

    // modal: Prestar contas (exige data de pagamento — regra real da API)
    const [prestarModalOpen, setPrestarModalOpen] = useState(false);
    const [prestarRow, setPrestarRow] = useState(null);
    const [paymentDate, setPaymentDate] = useState(dayjs());
    const [statusActionLoading, setStatusActionLoading] = useState(false);

    // modal: Voltar para pendente
    const [revertConfirmOpen, setRevertConfirmOpen] = useState(false);
    const [revertRow, setRevertRow] = useState(null);

    // modal: Excluir (endpoint ainda não existe — ver nota no topo do arquivo)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [transactionToDelete, setTransactionToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const canPutExpenseStatus = hasPermission('accounts.payable.update');
    const canPutTransaction =
        hasPermission('accounts.payable.update') ||
        hasPermission('accounts.receivable.update');
    const canCreateTransaction =
        hasPermission('accounts.receivable.create') ||
        hasPermission('accounts.payable.create');

    const showSuccess = (text) => {
        setSnackbarSeverity('success');
        setSnackbarMessage(text);
        setOpenSnackbar(true);
    };
    const showError = (text) => {
        setSnackbarSeverity('error');
        setSnackbarMessage(text || 'Não foi possível concluir a operação.');
        setOpenSnackbar(true);
    };

    // Busca a página atual no servidor sempre que filtros aplicados/página/tamanho mudam.
    // Período, tipo, status e busca são todos filtrados no servidor, e os totais
    // (receitas/despesas) vêm calculados sobre o conjunto filtrado inteiro.
    useEffect(() => {
        let cancelled = false;
        // Mobile: lista contínua ("Carregar mais"); ao recarregar volta para a 1ª página.
        const reloaded = lastReloadRef.current !== reloadKey;
        lastReloadRef.current = reloadKey;
        if (isMobileShell && reloaded && page > 0) {
            setPage(0);
            return undefined;
        }
        const params = {
            page: page + 1,
            page_size: isMobileShell ? MOBILE_PAGE_SIZE : pageSize,
            start_month: appliedPeriod.start.format('DD/MM/YYYY'),
            end_month: appliedPeriod.end.format('DD/MM/YYYY'),
        };
        if (type !== 'tudo') params.kind = type;
        if (status) params.status = status;
        if (search) params.search = search;

        setLoading(true);
        httpClient.get('/transactions/query', { params })
            .then((response) => {
                if (cancelled) return;
                const rows = response.data?.items || [];
                const total = response.data?.total || 0;
                // Página ficou além do fim (ex.: baixa/edição esvaziou a última página).
                if (!isMobileShell && rows.length === 0 && total > 0 && page > 0) {
                    setPage(Math.ceil(total / pageSize) - 1);
                    return;
                }
                if (isMobileShell && page > 0) setTransactions((prev) => [...prev, ...rows]);
                else setTransactions(rows);
                setTotalCount(total);
                setTotalRevenue(response.data?.total_revenue || '0,00');
                setTotalExpense(response.data?.total_expense || '0,00');
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Error fetching transactions:', error);
                setTransactions([]);
                setTotalCount(0);
                setTotalRevenue('0,00');
                setTotalExpense('0,00');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [page, pageSize, appliedPeriod, type, status, search, reloadKey, isMobileShell]);

    const applyFilters = () => {
        const nextType = draftType;
        const nextStatus = nextType === 'receita' ? '' : draftStatus;
        const nextSearch = draftSearch.trim();
        setType(nextType);
        setStatus(nextStatus);
        setSearch(nextSearch);
        setDraftStatus(nextStatus);
        setDraftSearch(nextSearch);
        setAppliedPeriod({ start: draftDateStart, end: draftDateEnd });
        setPage(0);
    };

    const clearFilters = () => {
        setDraftDateStart(defaultStart);
        setDraftDateEnd(defaultEnd);
        setDraftType('tudo');
        setDraftStatus('');
        setDraftSearch('');
        setType('tudo');
        setStatus('');
        setSearch('');
        setAppliedPeriod({ start: defaultStart, end: defaultEnd });
        setPage(0);
    };

    const periodIsDefault = (start, end) => Boolean(
        start?.isValid?.() && end?.isValid?.()
        && start.isSame(defaultStart, 'day') && end.isSame(defaultEnd, 'day'),
    );
    const removePeriodFilter = () => {
        setDraftDateStart(defaultStart);
        setDraftDateEnd(defaultEnd);
        setAppliedPeriod({ start: defaultStart, end: defaultEnd });
        setPage(0);
    };
    const removeTypeFilter = () => {
        setDraftType('tudo');
        setType('tudo');
        setPage(0);
    };
    const removeStatusFilter = () => {
        setDraftStatus('');
        setStatus('');
        setPage(0);
    };
    const removeSearchFilter = () => {
        setDraftSearch('');
        setSearch('');
        setPage(0);
    };

    // ---- Shell mobile (protótipo `financeiro/lancamentos.html`) ----
    const [mobileSearch, setMobileSearch] = useState('');
    // A busca é aplicada sozinha (debounce), sem botão "Buscar".
    useEffect(() => {
        if (!isMobileShell || mobileSearch.trim() === search) return undefined;
        const timer = setTimeout(() => {
            const next = mobileSearch.trim();
            setSearch(next);
            setDraftSearch(next);
            setPage(0);
        }, 350);
        return () => clearTimeout(timer);
    }, [mobileSearch, search, isMobileShell]);

    // Aviso vindo do formulário de novo lançamento (`/transactions/new`).
    useEffect(() => {
        const toast = location.state?.toast;
        if (!toast) return;
        showSuccess(toast);
        navigate(location.pathname, { replace: true, state: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const handleMobileChip = (value) => {
        setMobileChip(value);
        setPage(0);
        if (value === 'month') {
            setDraftType('tudo');
            setType('tudo');
            setDraftDateStart(defaultStart);
            setDraftDateEnd(defaultEnd);
            setAppliedPeriod({ start: defaultStart, end: defaultEnd });
            return;
        }
        const nextType = value === 'in' ? 'receita' : value === 'out' ? 'despesa' : 'tudo';
        setDraftType(nextType);
        setType(nextType);
        if (nextType === 'receita') {
            setDraftStatus('');
            setStatus('');
        }
    };

    const handleMobileApplyFilters = ({ status: nextStatus, start, end }) => {
        const nextStart = start || defaultStart;
        const nextEnd = end || defaultEnd;
        setStatus(type === 'receita' ? '' : nextStatus);
        setDraftStatus(type === 'receita' ? '' : nextStatus);
        setDraftDateStart(nextStart);
        setDraftDateEnd(nextEnd);
        setAppliedPeriod({ start: nextStart, end: nextEnd });
        setPage(0);
    };

    const handleMobileClearFilters = () => {
        clearFilters();
        setMobileSearch('');
        setMobileChip('all');
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(0);
    };

    // Recarrega a página corrente (a lógica do efeito recua de página se ela esvaziar).
    const refreshAfterMutation = async () => setReloadKey((key) => key + 1);

    // ---- drawer: Nova transação ----
    const openNewTransactionSheet = () => {
        setEditSheetOpen(false);
        setEditTransaction(null);
        setFullEditSheetOpen(false);
        setFullEditTransaction(null);
        setSheetOpen(true);
    };
    const closeFormSheet = () => setSheetOpen(false);
    const handleFormSheetSuccess = async (payload) => {
        const kind = typeof payload === 'string' ? payload : payload?.kind;
        const invalidateCashFlow = typeof payload === 'object' && payload?.invalidateCashFlow;
        closeFormSheet();
        await refreshAfterMutation();
        if (invalidateCashFlow) dispatchCashFlowInvalidate();
        showSuccess(kind === 'revenue' ? 'Receita cadastrada com sucesso!' : 'Despesa cadastrada com sucesso!');
    };

    // ---- drawer: Editar (variante compacta, `TransactionEditForm.js` — só
    // despesa Pendente/Vencido) ou completa (`Form.js` com tipo travado —
    // Receita qualquer status, ou Despesa já Prestado Contas). "Editar"
    // dispara sempre `openEditForRow`, que decide a variante pela mesma
    // regra do protótipo (`onEditTransaction`). ----
    const openEditSheet = (row) => {
        setSheetOpen(false);
        setEditTransaction(row);
        setEditSheetOpen(true);
    };
    const closeEditSheet = () => {
        setEditSheetOpen(false);
        setEditTransaction(null);
    };
    const handleEditSheetSuccess = async (result) => {
        closeEditSheet();
        await refreshAfterMutation();
        showSuccess(
            result?.paidNow
                ? 'Despesa atualizada e marcada como paga (Prestado Contas).'
                : 'Despesa atualizada com sucesso.'
        );
    };

    const openFullEditSheet = (row) => {
        setSheetOpen(false);
        setFullEditTransaction(row);
        setFullEditSheetOpen(true);
    };
    const closeFullEditSheet = () => {
        setFullEditSheetOpen(false);
        setFullEditTransaction(null);
    };
    const handleFullEditSheetSuccess = async () => {
        closeFullEditSheet();
        await refreshAfterMutation();
        showSuccess(fullEditTransaction?.type === 'receita' ? 'Receita atualizada com sucesso!' : 'Despesa atualizada com sucesso!');
    };

    const openEditForRow = (row) => {
        if (row.type === 'despesa' && isEditableExpenseStatus(row.status)) {
            openEditSheet(row);
        } else {
            openFullEditSheet(row);
        }
    };

    // ---- dialog: Visualizar ----
    const openViewDialog = (row) => {
        setViewTransaction(row);
        setViewOpen(true);
    };
    const closeViewDialog = () => {
        setViewOpen(false);
        setViewTransaction(null);
    };

    // ---- Prestar contas ----
    const openPrestarModal = (row) => {
        setPrestarRow(row);
        setPaymentDate(dayjs());
        setPrestarModalOpen(true);
    };
    const handleConfirmPrestarAccounts = async () => {
        if (statusActionLoading || !prestarRow?.id) return;
        const iso = toIsoYyyyMmDd(paymentDate);
        if (!iso) {
            showError('Selecione uma data de pagamento válida.');
            return;
        }
        setStatusActionLoading(true);
        try {
            await updateExpenseStatus({ id: prestarRow.id, status: EXPENSE_STATUS.PRESTADO_CONTAS, payment_date: iso });
            dispatchCashFlowInvalidate();
            await refreshAfterMutation();
            setPrestarModalOpen(false);
            setPrestarRow(null);
            showSuccess('Contas prestadas com sucesso. A saída aparecerá no fluxo de caixa no mês da data de pagamento informada.');
        } catch (e) {
            showError(e.message || 'Erro ao atualizar status.');
        } finally {
            setStatusActionLoading(false);
        }
    };

    // ---- Voltar para pendente ----
    const openRevertModal = (row) => {
        setRevertRow(row);
        setRevertConfirmOpen(true);
    };
    const handleConfirmRevertPending = async () => {
        if (statusActionLoading || !revertRow?.id) return;
        setStatusActionLoading(true);
        try {
            await updateExpenseStatus({ id: revertRow.id, status: EXPENSE_STATUS.PENDENTE });
            dispatchCashFlowInvalidate();
            await refreshAfterMutation();
            setRevertConfirmOpen(false);
            setRevertRow(null);
            showSuccess('Despesa voltou para Pendente e deixa de contar no fluxo de caixa até prestar contas novamente.');
        } catch (e) {
            showError(e.message || 'Erro ao atualizar status.');
        } finally {
            setStatusActionLoading(false);
        }
    };

    // ---- Excluir (endpoint ainda não existe no backend — ver nota no topo) ----
    const openDeleteDialog = (row) => {
        setTransactionToDelete(row);
        setDeleteDialogOpen(true);
    };
    const handleDelete = async () => {
        if (!transactionToDelete?.id) return;
        setDeleting(true);
        try {
            const response = await httpClient.delete(`/transactions/${transactionToDelete.id}`);
            if (response.status === 200 || response.status === 204) {
                showSuccess('Transação excluída com sucesso!');
                dispatchCashFlowInvalidate();
                await refreshAfterMutation();
                setDeleteDialogOpen(false);
                setTransactionToDelete(null);
            } else {
                showError('Não foi possível excluir a transação — endpoint de exclusão ainda não existe no backend.');
            }
        } catch (error) {
            console.error('Error deleting transaction:', error);
            showError(error.response?.data || error.message || 'Não foi possível excluir a transação — endpoint de exclusão ainda não existe no backend.');
        } finally {
            setDeleting(false);
        }
    };

    const columns = [
        {
            name: 'Tipo',
            path: 'type',
            render: (row) => <StatusChip domain="transactionType" value={row.type} />
        },
        {
            name: 'Descrição',
            path: 'description',
            render: (row) => (
                <Box
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280, fontSize: '13px', fontWeight: 700, color: 'text.primary' }}
                    title={row.description}
                >
                    {row.description}
                </Box>
            )
        },
        {
            name: 'Data',
            path: 'date_created',
            render: (row) => <Typography component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>{row.date_created}</Typography>
        },
        {
            name: 'Valor',
            path: 'amount',
            render: (row) => <ValueText transaction={row} />
        },
        {
            name: 'Status',
            path: 'status',
            render: (row) => (
                row.type !== 'despesa'
                    ? <Typography component="span" sx={{ color: 'text.disabled' }}>—</Typography>
                    : <StatusChip domain="expenseStatus" value={row.status} />
            )
        }
    ];

    const actions = useMemo(() => {
        const list = [];
        if (canPutExpenseStatus) {
            list.push({
                index: 'prestar-contas',
                filterRow: (row) => row.type === 'despesa',
                ruleShow: ['pendente', 'vencido'],
                icon: <CheckCircle2 size={16} strokeWidth={2} color={theme.palette.success.main} />,
                props: { tooltip: 'Prestar contas', onClick: (row) => openPrestarModal(row) }
            });
            list.push({
                index: 'voltar-pendente',
                filterRow: (row) => row.type === 'despesa',
                ruleShow: ['prestado contas'],
                icon: <RotateCcw size={16} strokeWidth={2} color={theme.palette.warning.main} />,
                props: { tooltip: 'Voltar para pendente', onClick: (row) => openRevertModal(row) }
            });
        }
        list.push({
            index: 'visualizar',
            icon: <Eye size={16} strokeWidth={2} />,
            props: { tooltip: 'Visualizar', onClick: (row) => openViewDialog(row) }
        });
        if (canPutTransaction) {
            list.push({
                index: 'editar',
                icon: <Pencil size={16} strokeWidth={2} />,
                props: { tooltip: 'Editar', onClick: (row) => openEditForRow(row) }
            });
        }
        list.push({
            index: 'excluir',
            icon: <Trash2 size={16} strokeWidth={2} color={theme.palette.error.main} />,
            props: { tooltip: 'Excluir', onClick: (row) => openDeleteDialog(row) }
        });
        return list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canPutExpenseStatus, canPutTransaction, theme]);

    // `DatePicker` (MUI X) renderiza um input "por seções" com classes
    // próprias (`MuiPickersOutlinedInput-*`/`MuiPickersSectionList-*`), não
    // as classes de `MuiOutlinedInput`/`MuiInputBase` de um `TextField`
    // comum — sem isso o campo cai no tamanho default do MUI em vez do
    // `tb-field` (13px, 40px de altura, 9px de raio) usado no Select/Busca
    // ao lado (mesma correção já aplicada em `pages/sales/List.js`).
    const TB_FIELD_RADIUS = '9px';

    const isViewRevenue = viewTransaction?.type === 'receita';
    const canEditViewTransaction = Boolean(viewTransaction && canPutTransaction);

    const sharedOverlays = (
        <>
            {/* drawer: Nova transação (`Form.js`, item irmão "Formulário de transação") */}
            <Drawer
                anchor="right"
                open={sheetOpen}
                onClose={closeFormSheet}
                slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' } } }}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 560, md: 640 }, maxWidth: '100vw',
                        borderRadius: 0,
                        boxShadow: theme.palette.mode === 'dark' ? '-8px 0 32px rgba(0,0,0,0.4)' : '-8px 0 32px rgba(0,0,0,0.12)',
                        borderLeft: '1px solid', borderColor: 'divider',
                        display: 'flex', flexDirection: 'column', overflow: 'hidden'
                    }
                }}
            >
                {sheetOpen && (
                    <TransactionForm embedded onClose={closeFormSheet} onSuccess={handleFormSheetSuccess} />
                )}
            </Drawer>

            {/* drawer: Editar (`TransactionEditForm.js` — variante compacta, despesa Pendente/Vencido) */}
            <Drawer
                anchor="right"
                open={editSheetOpen}
                onClose={closeEditSheet}
                slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' } } }}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 560, md: 640 }, maxWidth: '100vw',
                        borderRadius: 0,
                        boxShadow: theme.palette.mode === 'dark' ? '-8px 0 32px rgba(0,0,0,0.4)' : '-8px 0 32px rgba(0,0,0,0.12)',
                        borderLeft: '1px solid', borderColor: 'divider',
                        display: 'flex', flexDirection: 'column', overflow: 'hidden'
                    }
                }}
            >
                {editSheetOpen && editTransaction && (
                    <TransactionEditForm
                        key={editTransaction.id}
                        transaction={editTransaction}
                        onClose={closeEditSheet}
                        onSuccess={handleEditSheetSuccess}
                        onListRefresh={refreshAfterMutation}
                    />
                )}
            </Drawer>

            {/* drawer: Editar (`Form.js` — variante completa, tipo travado; Receita qualquer status ou Despesa já Prestado Contas) */}
            <Drawer
                anchor="right"
                open={fullEditSheetOpen}
                onClose={closeFullEditSheet}
                slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' } } }}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 560, md: 640 }, maxWidth: '100vw',
                        borderRadius: 0,
                        boxShadow: theme.palette.mode === 'dark' ? '-8px 0 32px rgba(0,0,0,0.4)' : '-8px 0 32px rgba(0,0,0,0.12)',
                        borderLeft: '1px solid', borderColor: 'divider',
                        display: 'flex', flexDirection: 'column', overflow: 'hidden'
                    }
                }}
            >
                {fullEditSheetOpen && fullEditTransaction && (
                    <TransactionForm
                        key={fullEditTransaction.id}
                        embedded
                        transaction={fullEditTransaction}
                        onClose={closeFullEditSheet}
                        onSuccess={handleFullEditSheetSuccess}
                        onListRefresh={refreshAfterMutation}
                    />
                )}
            </Drawer>

            {/* drawer: Visualizar (somente leitura) */}
            <Drawer
                anchor="right"
                open={viewOpen}
                onClose={closeViewDialog}
                slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' } } }}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 560, md: 640 }, maxWidth: '100vw',
                        borderRadius: 0,
                        boxShadow: theme.palette.mode === 'dark' ? '-8px 0 32px rgba(0,0,0,0.4)' : '-8px 0 32px rgba(0,0,0,0.12)',
                        borderLeft: '1px solid', borderColor: 'divider',
                        display: 'flex', flexDirection: 'column', overflow: 'hidden'
                    }
                }}
            >
                {viewTransaction && (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Box sx={{ p: '22px 22px 16px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.25, flexShrink: 0 }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Box sx={{
                                    width: 38, height: 38, borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: isViewRevenue ? 'success.bg' : 'error.bg', color: isViewRevenue ? 'success.main' : 'error.main', mb: '10px',
                                }}>
                                    <ArrowLeftRight size={18} strokeWidth={2} />
                                </Box>
                                <Typography sx={{ fontSize: '17px', fontWeight: 800, mb: '4px' }}>{viewTransaction.description || '—'}</Typography>
                                <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>Detalhes do lançamento registrado.</Typography>
                            </Box>
                            <IconButton onClick={closeViewDialog} size="small" aria-label="Fechar"><X size={15} strokeWidth={2} /></IconButton>
                        </Box>

                        <Box sx={{ p: '20px 22px', flex: 1, overflowY: 'auto' }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '14px 18px' }}>
                                <DetailItem label="Tipo" value={<StatusChip domain="transactionType" value={viewTransaction.type} />} />
                                <DetailItem label="Data" value={viewTransaction.date_created || '—'} />
                                <DetailItem label="Valor" value={<ValueText transaction={viewTransaction} />} />
                                <DetailItem
                                    label="Status"
                                    value={viewTransaction.type === 'despesa' ? <StatusChip domain="expenseStatus" value={viewTransaction.status} /> : '—'}
                                    muted={viewTransaction.type !== 'despesa'}
                                />
                                {viewTransaction.type === 'despesa' && (
                                    <DetailItem
                                        label="Vencimento"
                                        value={viewTransaction.due_date || 'Não disponível'}
                                        muted={!viewTransaction.due_date}
                                    />
                                )}
                                <DetailItem full label="Descrição" value={viewTransaction.description || '—'} />
                            </Box>
                        </Box>

                        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1.25, justifyContent: 'flex-end', flexShrink: 0 }}>
                            <Button variant="outlined" onClick={closeViewDialog}>Fechar</Button>
                            {canEditViewTransaction && (
                                <Button variant="contained" onClick={() => { const row = viewTransaction; closeViewDialog(); openEditForRow(row); }}>
                                    Editar
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}
            </Drawer>

            {/* modal: Prestar contas (exige data de pagamento) */}
            <DialogForm
                open={prestarModalOpen}
                iconType="info"
                title="Prestar contas"
                subtitle={prestarRow?.description || undefined}
                content="Informe a data de pagamento (obrigatória). A saída aparecerá no fluxo de caixa no mês dessa data."
                confirmButtonText="Confirmar"
                cancelButtonText="Cancelar"
                loading={statusActionLoading}
                loadingText="Salvando…"
                onClose={() => { if (statusActionLoading) return; setPrestarModalOpen(false); setPrestarRow(null); }}
                onSubmit={() => { void handleConfirmPrestarAccounts(); }}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                    <DatePicker
                        label="Data de pagamento"
                        format="DD/MM/YYYY"
                        value={paymentDate}
                        onChange={(v) => setPaymentDate(v || dayjs())}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                size: 'small',
                                sx: {
                                    '& .MuiPickersOutlinedInput-root': { height: 40, borderRadius: '9px' },
                                    '& .MuiPickersSectionList-sectionContent, & .MuiPickersInputBase-sectionsContainer': { fontSize: '13px' },
                                }
                            }
                        }}
                    />
                </LocalizationProvider>
            </DialogForm>

            {/* modal: Voltar para pendente */}
            <DialogForm
                open={revertConfirmOpen}
                iconType="warning"
                title="Voltar para pendente?"
                content="A despesa deixa de contar como saída no fluxo de caixa até que seja prestada contas novamente com uma data de pagamento."
                confirmButtonText="Confirmar"
                cancelButtonText="Cancelar"
                loading={statusActionLoading}
                loadingText="Salvando…"
                onClose={() => { if (statusActionLoading) return; setRevertConfirmOpen(false); setRevertRow(null); }}
                onSubmit={() => { void handleConfirmRevertPending(); }}
            />

            {/* modal: Excluir — endpoint ainda não existe no backend (ver nota no topo do arquivo) */}
            <DialogForm
                iconType="warning"
                open={deleteDialogOpen}
                title="Excluir Lançamento"
                content={`Tem certeza que deseja excluir o lançamento "${transactionToDelete?.description || ''}"?`}
                secondaryContent="Esta ação não pode ser desfeita."
                confirmButtonText="Sim, Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
                loadingText="Removendo..."
                onClose={() => { if (deleting) return; setDeleteDialogOpen(false); setTransactionToDelete(null); }}
                onSubmit={() => { void handleDelete(); }}
            />
        </>
    );

    // Shell mobile: lista do protótipo `financeiro/lancamentos.html` (mesmos dados, filtros e ações).
    if (isMobileShell) {
        const rowActions = (row) => [
            ...(canPutExpenseStatus && row.type === 'despesa' && ['pendente', 'vencido'].includes(String(row.status || '').toLowerCase())
                ? [{ label: 'Prestar contas', icon: CheckIcon, onClick: () => openPrestarModal(row) }] : []),
            ...(canPutExpenseStatus && row.type === 'despesa' && String(row.status || '').toLowerCase() === 'prestado contas'
                ? [{ label: 'Voltar para pendente', icon: UndoIcon, onClick: () => openRevertModal(row) }] : []),
            { label: 'Visualizar', icon: EyeIcon, onClick: () => openViewDialog(row) },
            ...(canPutTransaction ? [{ label: 'Editar', icon: EditIcon, onClick: () => openEditForRow(row) }] : []),
            { label: 'Excluir', icon: TrashIcon, destructive: true, onClick: () => openDeleteDialog(row) },
        ];
        return (
            <PageScaffold
                links={[]}
                allowBleed
                titlePageTarget={'Lançamentos'}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            >
                <MobileTransactionsList
                    transactions={transactions}
                    loading={loading}
                    hasMore={transactions.length < totalCount}
                    onLoadMore={() => setPage((current) => current + 1)}
                    totalRevenue={totalRevenue}
                    totalExpense={totalExpense}
                    chip={mobileChip}
                    onChipChange={handleMobileChip}
                    search={mobileSearch}
                    onSearchChange={setMobileSearch}
                    type={type}
                    status={status}
                    period={appliedPeriod}
                    filtersActive={Boolean(status) || !appliedPeriod.start.isSame(defaultStart, 'day') || !appliedPeriod.end.isSame(defaultEnd, 'day')}
                    onApplyFilters={handleMobileApplyFilters}
                    onClearFilters={handleMobileClearFilters}
                    canCreate={canCreateTransaction}
                    onNew={() => navigate('/transactions/new')}
                    getActions={rowActions}
                />
                {sharedOverlays}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[]}
            titlePageTarget={'Lançamentos'}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity={snackbarSeverity}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head */}
                <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38, height: 38, borderRadius: '11px', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', background: (t) => t.palette.gradient.brand,
                        }}>
                            <ArrowLeftRight size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Lançamentos
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Registre e acompanhe as receitas e despesas financeiras do seu negócio.
                            </Typography>
                        </Box>
                    </Box>

                    {canCreateTransaction && (
                        <NewItemButton label="Novo lançamento" onClick={openNewTransactionSheet} />
                    )}
                </Box>

                {/* report-toolbar */}
                <FilterToolbar
                    activeCount={(draftType !== 'tudo' ? 1 : 0) + (draftStatus ? 1 : 0) + (periodIsDefault(draftDateStart, draftDateEnd) ? 0 : 1)}
                    onClear={clearFilters}
                    onApply={applyFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 240px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Descrição do lançamento..."
                                value={draftSearch}
                                onChange={(e) => setDraftSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search size={15} strokeWidth={2} /></InputAdornment>,
                                    sx: { height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" size="small" onClick={clearFilters} sx={{ height: '33px' }}>Limpar</Button>
                            <Button variant="contained" size="small" startIcon={<Search size={14} strokeWidth={2} />} onClick={applyFilters} sx={{ height: '33px' }}>
                                Buscar
                            </Button>
                        </Stack>
                    )}
                >
                        <Box>
                            <TbLabel>Período</TbLabel>
                            <DesktopPeriodSelector
                                start={draftDateStart}
                                end={draftDateEnd}
                                onChange={(start, end) => {
                                    setDraftDateStart(start);
                                    setDraftDateEnd(end);
                                }}
                                showShortcuts={false}
                                showRefresh={false}
                                align="start"
                                dualMonth
                            />
                        </Box>

                        <Box>
                            <TbLabel>Tipo</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <Select
                                    value={draftType}
                                    displayEmpty
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setDraftType(value);
                                        if (value === 'receita') setDraftStatus('');
                                    }}
                                    renderValue={() => (draftType === 'receita' ? 'Receita' : draftType === 'despesa' ? 'Despesa' : 'Todos')}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                                >
                                    <MenuItem value="tudo">Todos</MenuItem>
                                    <MenuItem value="receita">Receita</MenuItem>
                                    <MenuItem value="despesa">Despesa</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Box>
                            <TbLabel>Status</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 160 }} disabled={draftType === 'receita'}>
                                <Select
                                    value={draftStatus}
                                    displayEmpty
                                    onChange={(e) => setDraftStatus(e.target.value)}
                                    renderValue={() => (draftStatus === 'pendente' ? 'Pendente' : draftStatus === 'prestado' ? 'Prestado Contas' : 'Todos')}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="pendente">Pendente</MenuItem>
                                    <MenuItem value="prestado">Prestado Contas</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                </FilterToolbar>

                {/* chips-row */}
                <FilterChips>
                    <Chip
                        label={`Período: ${appliedPeriod.start.format('DD/MM/YYYY')} – ${appliedPeriod.end.format('DD/MM/YYYY')}`}
                        size="small"
                        onDelete={periodIsDefault(appliedPeriod.start, appliedPeriod.end) ? undefined : removePeriodFilter}
                        deleteIcon={<X size={12} strokeWidth={2.5} />}
                        sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                    />
                    {type !== 'tudo' && (
                        <Chip
                            label={`Tipo: ${type === 'receita' ? 'Receita' : 'Despesa'}`}
                            size="small"
                            onDelete={removeTypeFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                    )}
                    {status && (
                        <Chip
                            label={`Status: ${status === 'pendente' ? 'Pendente' : 'Prestado Contas'}`}
                            size="small"
                            onDelete={removeStatusFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {search && (
                        <Chip
                            label={`Busca: "${search}"`}
                            size="small"
                            onDelete={removeSearchFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${totalCount} lançamento${totalCount === 1 ? '' : 's'} encontrado${totalCount === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                    <Chip label={`Total receitas: R$ ${totalRevenue}`} size="small" sx={{ bgcolor: 'success.bg', color: 'success.main' }} />
                    <Chip label={`Total despesas: R$ ${totalExpense}`} size="small" sx={{ bgcolor: 'error.bg', color: 'error.main' }} />
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={transactions}
                    iconsActions={actions}
                    loading={loading}
                    onRowClick={(row) => openViewDialog(row)}
                    rowPerPage={pageSize}
                    rowsPerPageOptions={[5, 8, 15]}
                    itemLabel="transações"
                    serverPagination={{
                        page,
                        rowsPerPage: pageSize,
                        total: totalCount,
                        onPageChange: setPage,
                        onRowsPerPageChange: handlePageSizeChange,
                    }}
                />
            </Box>

            {sharedOverlays}
        </PageScaffold>
    );
}
